import React, { useState, useEffect } from "react";
import { produtos, downloadExcel, exportData, fornecedores } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Download, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

export function ProdutosPage() {
  const [data, setData] = useState([]);
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    codigo_interno: "",
    descricao: "",
    categoria: "",
    marca: "",
    unidade_medida: "UN",
    aplicacao: "",
    ncm: "",
    fornecedor_principal_id: "",
    pais_origem: "",
    custo_unitario_compra: 0,
    preco_venda_padrao: 0,
    margem_percentual_padrao: 0,
  });

  const fetchData = async () => {
    try {
      const [produtosRes, fornecedoresRes] = await Promise.all([
        produtos.list(),
        fornecedores.list(),
      ]);
      setData(produtosRes.data);
      setFornecedoresList(fornecedoresRes.data);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await produtos.update(editingItem.id, formData);
        toast.success("Produto atualizado!");
      } else {
        await produtos.create(formData);
        toast.success("Produto criado!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      codigo_interno: item.codigo_interno || "",
      descricao: item.descricao || "",
      categoria: item.categoria || "",
      marca: item.marca || "",
      unidade_medida: item.unidade_medida || "UN",
      aplicacao: item.aplicacao || "",
      ncm: item.ncm || "",
      fornecedor_principal_id: item.fornecedor_principal_id || "",
      pais_origem: item.pais_origem || "",
      custo_unitario_compra: item.custo_unitario_compra || 0,
      preco_venda_padrao: item.preco_venda_padrao || 0,
      margem_percentual_padrao: item.margem_percentual_padrao || 0,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    try {
      await produtos.delete(id);
      toast.success("Produto excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      codigo_interno: "",
      descricao: "",
      categoria: "",
      marca: "",
      unidade_medida: "UN",
      aplicacao: "",
      ncm: "",
      fornecedor_principal_id: "",
      pais_origem: "",
      custo_unitario_compra: 0,
      preco_venda_padrao: 0,
      margem_percentual_padrao: 0,
    });
  };

  const handleExport = async () => {
    try {
      await downloadExcel(exportData.produtos, "produtos.xlsx");
      toast.success("Exportado!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const filteredData = data.filter(
    (item) =>
      item.descricao?.toLowerCase().includes(search.toLowerCase()) ||
      item.codigo_interno?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { key: "codigo_interno", header: "Código", width: "150px" },
    { 
      key: "descricao", 
      header: "Descrição",
      render: (v) => (
        <span className="truncate max-w-[300px] block font-medium" title={v}>
          {v}
        </span>
      )
    },
    { key: "categoria", header: "Categoria" },
    { key: "marca", header: "Marca" },
    { key: "unidade_medida", header: "Unidade", width: "80px" },
    { key: "ncm", header: "NCM", width: "100px" },
    {
      key: "status",
      header: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="produtos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Produtos</h1>
          <p className="text-muted-foreground">Gerencie seu portfólio de produtos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-produtos-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-produto-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Produto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingItem ? "Editar Produto" : "Novo Produto"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Código Interno *</Label>
                      <Input
                        value={formData.codigo_interno}
                        onChange={(e) => setFormData({ ...formData, codigo_interno: e.target.value })}
                        required
                        data-testid="produto-codigo"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição *</Label>
                      <Input
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        required
                        data-testid="produto-descricao"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria</Label>
                      <Input
                        value={formData.categoria}
                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        data-testid="produto-categoria"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Marca</Label>
                      <Input
                        value={formData.marca}
                        onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                        data-testid="produto-marca"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade</Label>
                      <Select value={formData.unidade_medida} onValueChange={(v) => setFormData({ ...formData, unidade_medida: v })}>
                        <SelectTrigger data-testid="produto-unidade">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UN">Unidade</SelectItem>
                          <SelectItem value="KG">Quilograma</SelectItem>
                          <SelectItem value="L">Litro</SelectItem>
                          <SelectItem value="M">Metro</SelectItem>
                          <SelectItem value="CX">Caixa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>NCM</Label>
                      <Input
                        value={formData.ncm}
                        onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
                        data-testid="produto-ncm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fornecedor Principal</Label>
                      <Select value={formData.fornecedor_principal_id} onValueChange={(v) => setFormData({ ...formData, fornecedor_principal_id: v })}>
                        <SelectTrigger data-testid="produto-fornecedor">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {fornecedoresList.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.razao_social}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>País de Origem</Label>
                      <Input
                        value={formData.pais_origem}
                        onChange={(e) => setFormData({ ...formData, pais_origem: e.target.value })}
                        data-testid="produto-pais"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="save-produto-btn">{editingItem ? "Salvar" : "Criar"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-produtos"
          />
        </div>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhum produto encontrado" />
    </div>
  );
}
